//client/src/contexts/LanguageContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'st';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations = {
  en: {
    // Header
    'header.appName': 'AgroSense',
    'header.tagline': 'Smart Agriculture Advisor',
    'header.online': 'Online',
    'header.offline': 'Offline',
    'header.signIn': 'Sign In',
    'header.signOut': 'Sign Out',
    'header.admin': 'Admin',
    'header.sendNotification': 'Send Notification',
    'header.notificationsOn': 'Notifications On',
    'header.notificationsOff': 'Notifications Off',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome to AgroSense',
    'dashboard.myFarms': 'My Farms',
    'dashboard.addFarm': 'Add Farm',
    'dashboard.weather': 'Weather',
    'dashboard.cropHealth': 'Crop Health',
    'dashboard.aiAdvisor': 'AI Advisor',
    'dashboard.marketInfo': 'Market Info',
    'dashboard.taskPlanner': 'Task Planner',
    'dashboard.yieldForecast': 'Yield Forecast',
    'dashboard.soilManagement': 'Soil Management',
    'dashboard.irrigation': 'Irrigation',
    'dashboard.fertilizerPlanner': 'Fertilizer Planner',
    'dashboard.pestControl': 'Pest Control',
    'dashboard.visibility': 'km Visibility',
    'dashboard.pestWeatherAlerts': 'Pest & Weather Alerts',
    'dashboard.authRequired': 'Pest and weather alerts require authentication.',
    'dashboard.personalizedRecommendations': 'Personalized Recommendations',
    'dashboard.personalizedAdvice': 'Your personalized farming advice will appear here.',
    'dashboard.signInAdvice': 'Sign in to get personalized farming advice',
    'dashboard.irrigationReminder': 'Irrigation Reminder',
    'dashboard.monitorSoil': 'Monitor soil moisture levels for optimal crop health.',
    'dashboard.weatherAlert': 'Weather Alert',
    'dashboard.signInRecommendations': 'Sign In for Personalized Recommendations',
    'dashboard.cropHealthAnalysis': 'Crop Health Analysis',
    'dashboard.uploadPhotos': 'Upload photos of your crops to get AI-powered health analysis and treatment recommendations.',
    'dashboard.addCropReport': '+ Add Crop Health Report',
    'dashboard.signInAnalyze': 'Sign in to Analyze Crops',
    'dashboard.signInQuestions': 'Sign In to Ask Questions',
    'dashboard.noFarmSelected': 'No farm selected for irrigation panel.',
    'dashboard.live': 'Live',
    'dashboard.currentWeather': 'Current Weather',
    'dashboard.farmLocation': 'Farm Location',
    'dashboard.temperature': 'Temperature',
    'dashboard.humidity': 'Humidity',
    'dashboard.wind': 'Wind (km/h)',
    
    // Stats
    'stats.activeFarms': 'Active Farms',
    'stats.totalArea': 'Total Area',
    'stats.regions': 'Regions',
    'stats.mostRecentFarm': 'Most Recent Farm',
    
    // Features
    'features.cropAnalysis': 'Crop Analysis',
    'features.weather': 'Weather',
    'features.pestControl': 'Pest Control',
    'features.voiceQuery': 'Voice Query',
    'features.signInRequired': 'Sign in required',
    
    // AI Advisor
    'ai.title': 'AI Farming Advisor',
    'ai.subtitle': 'Ask me anything about agriculture',
    'ai.placeholder': 'Ask me anything about farming...',
    'ai.examples': 'Examples: "Best crops for rainy season", "How to treat aphids?"',
    'ai.poweredBy': 'Powered by Cohere AI',
    'ai.typing': 'AI is thinking...',
    'ai.welcome': 'Hello Farmer!',
    'ai.welcomeText': "I'm your AI farming assistant. Ask me about crops, weather, pests, or anything agriculture-related. I can also help with best practices and general agricultural advice.",
    'ai.startTyping': 'Start by typing your question in the input field below.',
    'ai.myChats': 'My Chats',
    'ai.newChat': 'New Chat',
    'ai.newFarmingChat': 'New Farming Chat',
    'ai.you': 'You',
    'ai.ai': 'AI',
    'ai.smartFarmingAI': '🌾 Smart Farming AI',
    'ai.intelligentAssistant': 'Intelligent agricultural assistant',
    'ai.researchOn': 'Research ON',
    'ai.researchOff': 'Research OFF',
    'ai.exportChat': 'Export chat',
    'ai.hideHistory': 'Hide chat history',
    'ai.showHistory': 'Show chat history',
    'ai.stopRecording': 'Stop recording',
    'ai.startRecording': 'Start voice recording',
    'ai.sendMessage': 'Send message',
    'ai.enterToSend': 'Press Enter to send • Shift+Enter for new line',
    'ai.offline': 'Offline',
    'ai.retrying': 'Retrying...',
    'ai.sources': 'Sources:',
    'ai.messages': 'messages',
    
    // Fertilizer
    'fertilizer.title': 'Fertilizer Planner',
    'fertilizer.subtitle': 'Optimize your crop nutrition',
    'fertilizer.signInRequired': 'Sign in to access personalized fertilizer recommendations',
    'fertilizer.signInButton': 'Sign In to Continue',
    'fertilizer.calculating': 'Calculating recommendations...',
    'fertilizer.noFarms': 'No Farms Found',
    'fertilizer.addFarms': 'Add farms to get fertilizer recommendations',
    'fertilizer.dataError': 'Unable to Load Data',
    'fertilizer.refreshData': 'Refresh Data',
    'fertilizer.noRecommendations': 'No recommendations available for your farms.',
    'fertilizer.ensureReadings': 'Ensure soil readings are available.',
    'fertilizer.retryRecommendations': 'Retry Recommendations',
    'fertilizer.soilImbalance': 'Soil Imbalance',
    'fertilizer.lastReading': 'Last reading:',
    'fertilizer.currentNutrients': 'Current Nutrient Levels',
    'fertilizer.recommendedApplication': 'Recommended Application',
    'fertilizer.urea': 'Urea (46-0-0)',
    'fertilizer.dap': 'DAP (18-46-0)',
    'fertilizer.mop': 'MOP (0-0-60)',
    'fertilizer.kg': 'kg/ha',
    'fertilizer.bestPractices': 'Best Practices',
    'fertilizer.practice1': 'Apply fertilizers during cool, calm weather conditions',
    'fertilizer.practice2': 'Water crops immediately after fertilizer application',
    'fertilizer.practice3': 'Split nitrogen applications for better uptake',
    'fertilizer.practice4': 'Test soil regularly to monitor nutrient levels',
    'fertilizer.practice5': 'Store fertilizers in a cool, dry place away from moisture',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.submit': 'Submit',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.view': 'View',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.refresh': 'Refresh',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.language': 'en',
    'common.welcome': 'Welcome',
    'common.realTimeData': 'Real-time data',
    'common.cachedData': 'Cached data',
    'common.quickActions': 'Quick Actions',
    'common.addPestAlert': 'Add Pest Alert',
    'common.viewHistory': 'View Analysis History',
    'common.statistics': 'Statistics',
    'common.features': 'Features',
    'common.yourFarms': 'Your Farms',
    'common.viewFarm': 'View farm',
    'common.noLocation': 'No location',
    'common.acres': 'acres',
    'common.recentReports': 'Recent Reports',
    'common.noReports': 'No reports yet.',
    'common.unknownFarm': 'Unknown Farm',
    'common.healthy': 'Healthy',
    'common.needsAttention': 'Needs Attention',
    'common.diseased': 'Diseased',
    'common.pestDetected': 'Pest Detected',
    'dashboard.navigationTabs': 'Dashboard Navigation',
    
    // Footer
    'footer.empowering': 'Empowering farmers with AI-driven insights for better crop management and sustainable agriculture.',
    'footer.quickLinks': 'Quick Links',
    'footer.cropAnalysis': 'Crop Analysis',
    'footer.weatherForecast': 'Weather Forecast',
    'footer.pestManagement': 'Pest Management',
    'footer.aiAdvisor': 'AI Advisor',
    'footer.resources': 'Resources',
    'footer.farmingGuide': 'Farming Guide',
    'footer.bestPractices': 'Best Practices',
    'footer.communityForum': 'Community Forum',
    'footer.supportCenter': 'Support Center',
    'footer.contactUs': 'Contact Us',
    'footer.support': 'support@agrosense.com',
    'footer.phone': '+1 (555) 123-4567',
    'footer.address': 'Agricultural Innovation Hub',
    'footer.copyright': '© 2024 AgroSense. All rights reserved. Built for farmers, by farmers.',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.termsOfService': 'Terms of Service',
    'footer.help': 'Help',
    
    // Weather Panel
    'weather.resultsFor': 'Results for',
    'weather.changeLocation': '(Change location)',
    'weather.feelsLike': 'Feels like',
    'weather.precip': 'Precip',
    'weather.wind': 'Wind',
    'weather.chooseLocation': 'Choose location',
    'weather.searchCity': 'Search for a city...',
    'weather.popularLocations': 'Popular locations:',
    'weather.useMyLocation': 'Use my device location',
    'weather.weatherUpdate': 'Weather will update for your selected location',
    'weather.loadingPrices': 'Loading market prices, please wait...',
    'weather.overview': 'overview',
    'weather.precipitation': 'precipitation',
    'weather.humidity': 'humidity',
    'weather.now': 'Now',
    'weather.todaysAmount': "Today's amount",
    'weather.todaysHigh': "Today's high",
    'weather.todaysAverage': "Today's average",
    'weather.clear': 'Clear',
    'weather.partlyCloudy': 'Partly Cloudy',
    'weather.cloudy': 'Cloudy',
    'weather.fog': 'Fog',
    'weather.drizzle': 'Drizzle',
    'weather.rainShowers': 'Rain Showers',
    'weather.thunderstorm': 'Thunderstorm',
    'weather.snow': 'Snow',
    'weather.rain': 'Rain',
    'weather.unknown': 'Unknown',
    
    // Market Info
    'market.title': 'Lesotho Maize Markets',
    'market.subtitle': 'Real-time maize prices across Lesotho\'s agricultural markets',
    'market.refreshData': 'Refresh Prices',
    'market.bestMarket': 'Best Market',
    'market.highestPrice': 'Highest maize price',
    'market.priceStability': 'Price Stability',
    'market.marketConditions': 'Market conditions',
    'market.recommendedAction': 'Recommended Action',
    'market.basedOnTrends': 'Based on trends',
    'market.loadingPrices': 'Loading market prices, please wait...',
    'market.errorFetching': 'Error fetching data:',
    'market.noData': 'No market price data is currently available for this period.',
    'market.tryRefresh': 'Try refreshing or check back later.',
    'market.commodity': 'Commodity',
    'market.market': 'Market',
    'market.price': 'Price',
    'market.unit': 'Unit',
    'market.date': 'Date',
    
    // Yield Forecast
    'yield.title': 'Yield Forecast',
    'yield.signInRequired': 'Sign in to access Yield Forecasting.',
    'yield.cropType': 'Crop Type',
    'yield.area': 'Area (acres)',
    'yield.getForecast': 'Get Forecast',
    'yield.forecasting': 'Forecasting...',
    'yield.error': 'Error:',
    'yield.predictedYield': 'Predicted yield for',
    'yield.over': 'over',
    'yield.acres': 'acres',
    'yield.maize': 'Maize',
    'yield.wheat': 'Wheat',
    'yield.sorghum': 'Sorghum',
    
    // Task Planner
    'tasks.title': 'Farm Task Planner',
    'tasks.subtitle': 'Organize your farming activities efficiently',
    'tasks.signInRequired': 'Please sign in to access your farm planning tasks.',
    'tasks.addNewTask': 'Add New Farm Task',
    'tasks.createNewTask': 'Create New Farm Task',
    'tasks.taskTitle': 'Task Title',
    'tasks.description': 'Description',
    'tasks.dueDate': 'Due Date',
    'tasks.required': '*',
    'tasks.titlePlaceholder': 'e.g., Plant maize in field A',
    'tasks.descriptionPlaceholder': 'Task details, location, or notes',
    'tasks.createTask': 'Create Task',
    'tasks.adding': 'Adding...',
    'tasks.pendingTasks': 'Pending Tasks',
    'tasks.completedTasks': 'Completed Tasks',
    'tasks.noPendingTasks': 'No pending tasks',
    'tasks.addFirstTask': 'Add your first farm task to get started',
    'tasks.loadingTasks': 'Loading farm tasks...',
    'tasks.taskError': 'Task Error',
    'tasks.today': 'Today',
    'tasks.tomorrow': 'Tomorrow',
    'tasks.yesterday': 'Yesterday',
    'tasks.inDays': 'in {days} days',
    'tasks.daysAgo': '{days} days ago',
    'tasks.save': 'Save',
    'tasks.edit': 'Edit Task',
    'tasks.delete': 'Delete Task',
    'tasks.deleteConfirm': 'Are you sure you want to delete this task?',
    
    // Soil Management
    'soil.title': 'Soil Management',
    'soil.subtitle': 'Monitor and optimize your soil health',
    'soil.signInRequired': 'Access detailed soil nutrient reports and recommendations for your farms.',
    'soil.signInButton': 'Sign in to Manage Soil',
    'soil.loadingData': 'Loading soil data...',
    'soil.noFarms': 'No Farms Found',
    'soil.addFarms': 'You need to add farms to your profile to view soil management data.',
    'soil.dataError': 'Error Loading All Data',
    'soil.tryAgain': 'Try Again',
    'soil.allFarms': 'All Farms',
    'soil.averageNitrogen': 'Average Nitrogen',
    'soil.averagePhosphorus': 'Average Phosphorus',
    'soil.averagePotassium': 'Average Potassium',
    'soil.low': 'Low',
    'soil.optimal': 'Optimal',
    'soil.yourFarms': 'Your Farms',
    'soil.noReadings': 'No soil readings available for your farms.',
    'soil.reloadReadings': 'Reload Readings',
    'soil.needsAttention': 'Needs Attention',
    'soil.lastTested': 'Last tested:',
    'soil.nitrogen': 'Nitrogen',
    'soil.phosphorus': 'Phosphorus',
    'soil.potassium': 'Potassium',
    'soil.lowNutrients': 'One or more nutrients are below optimal levels. Consider adding supplements.',
    'soil.healthRecommendations': 'Soil Health Recommendations',
    'soil.waterManagement': 'Water Management',
    'soil.waterManagementDesc': 'Implement efficient irrigation to conserve water and maintain consistent soil moisture levels.',
    'soil.fertilizationPlan': 'Fertilization Plan',
    'soil.fertilizationDesc': 'Apply a balanced NPK fertilizer based on soil test results. Use organic compost to maintain nutrient levels.',
    'soil.cropRotation': 'Crop Rotation',
    'soil.cropRotationDesc': 'Rotate legumes with cereals to naturally fix nitrogen and break pest cycles. Consider planting cover crops.',
    'soil.testingSchedule': 'Testing Schedule',
    'soil.testingScheduleDesc': 'Conduct soil tests every 3-6 months for optimal monitoring and management.',
    'soil.recentTests': 'Recent Soil Tests',
    'soil.noRecentData': 'No recent soil test data to display.',
    'soil.farm': 'Farm',
    'soil.date': 'Date',
    'soil.status': 'Status',
    'soil.critical': 'Critical',
    
    // Privacy Settings
    'privacy.title': 'Privacy Settings',
    'privacy.dataRetention': 'Data Retention',
    'privacy.dataRetentionDesc': 'Choose how long you want to keep your data',
    'privacy.analytics': 'Analytics',
    'privacy.analyticsDesc': 'Allow using your data for service improvement',
    'privacy.marketing': 'Marketing',
    'privacy.marketingDesc': 'Receive notifications about new products and services',
    'privacy.partners': 'Partners',
    'privacy.partnersDesc': 'Share data with trusted partners for enhanced services',
    'privacy.saveSettings': 'Save Settings',
    'privacy.saved': 'Saved!',
    'common.months': 'months',
    'common.year': 'year',
    'common.years': 'years',
    'common.authRequired': 'Authentication required',
    'common.takePhoto': 'Take Photo',
    'common.capture': 'Capture',
    'common.analysisResult': 'Analysis Result',
    'common.takeUploadPhoto': 'Take or upload a photo to analyze crop health',
    'common.analysisHistory': 'Analysis History',
    'common.noAnalysisHistory': 'No analysis history yet.',
    'common.lastAnalyses': 'Your last 3 analyses will appear here.',
    'common.home': 'Home',
    
    // Offline Support
    'offline.online': 'Online',
    'offline.offline': 'Offline',
    'offline.syncing': 'Syncing',
    'offline.connected': 'Connected to Internet',
    'offline.disconnected': 'No Internet Connection',
    'offline.workingOffline': 'Working Offline',
    'offline.offlineMessage': 'You can continue using AgroSense. Your data will sync when you reconnect.',
    'offline.pendingSync': '{{count}} items waiting to sync',
    'offline.syncMessage': 'These will be uploaded when you have internet.',
    'offline.allSynced': 'All data synced',
    'offline.syncedMessage': 'Your data is up to date.',
    'offline.lastOnline': 'Last online',
    'offline.dataFromCache': 'Showing saved data (offline)',
    'offline.willSyncLater': 'Changes will sync when online',
    
    // AI Error Messages
    'ai.error.pleaseEnterMessage': 'Please enter a message',
    'ai.error.messageTooLong': 'Message too long (max 2000 characters)',
    'ai.error.useEnglishSesotho': 'Please use English or Sesotho only',
    'ai.error.useAppropriateLanguage': 'Please use appropriate language',
    'ai.error.speechNotSupported': 'Speech recognition not supported in this browser',
    'ai.error.micAccessDenied': 'Microphone access denied. Please allow microphone access.',
    'ai.error.noMicrophoneFound': 'No microphone found. Please connect a microphone.',
    'ai.error.recordingFailed': 'Failed to start recording. Please try again.',
    'ai.error.speechRequiresInternet': 'Speech recognition requires internet connection',
    'ai.error.transcriptionFailed': 'Failed to transcribe audio. Please try typing instead.',
    'ai.error.ttsNotSupported': 'Text-to-speech not supported in this browser',
    'ai.error.audioPlaybackFailed': 'Failed to play audio. Please try again.',
    'ai.error.noInternet': 'No internet connection. Please check your network.',
    'ai.error.tooManyRequests': 'Too many requests. Please wait a moment.',
    'ai.error.serviceUnavailable': 'AI service temporarily unavailable. Please try again.',
    'ai.error.invalidResponse': 'Invalid response from AI service',
    'ai.error.networkRetry': 'Network error. Retrying... ({count}/3)',
    'ai.error.failedToGetResponse': 'Failed to get AI response',
    'ai.error.failedToCopy': 'Failed to copy message',
    'ai.error.voiceNotSupported': 'Voice not supported',
    'ai.error.audioRecordedImplementing': 'Audio recorded - implementing speech-to-text...',
    'ai.offlineMessage': 'AgroSense AI is working offline with cached responses.',
    'ai.disclaimer': 'AgroSense AI can make mistakes. Consider checking important information.',
    
    // Admin Dashboard
    'admin.dashboard': 'Admin Dashboard',
    'admin.welcome': 'Welcome',
    'admin.manageUsers': 'Manage AgroSense platform and users',
    'admin.refresh': 'Refresh',
    'admin.overview': 'Overview',
    'admin.users': 'Users',
    'admin.notifications': 'Notifications',
    'admin.logs': 'Logs',
    'admin.settings': 'Settings',
    'admin.accessDenied': 'Access Denied',
    'admin.noPrivileges': "You don't have admin privileges to access this dashboard.",
    'admin.loadingData': 'Loading admin data...',
    'admin.totalUsers': 'Total Users',
    'admin.activeUsers': 'Active Users',
    'admin.blockedUsers': 'Blocked Users',
    'admin.notificationsSent': 'Notifications Sent',
    'admin.systemHealth': 'System Health',
    'admin.healthy': 'healthy',
    'admin.warning': 'warning',
    'admin.critical': 'critical',
    'admin.searchUsers': 'Search users...',
    'admin.allUsers': 'All Users',
    'admin.active': 'Active',
    'admin.blocked': 'Blocked',
    'admin.user': 'User',
    'admin.contact': 'Contact',
    'admin.status': 'Status',
    'admin.actions': 'Actions',
    'admin.joined': 'Joined',
    'admin.block': 'Block',
    'admin.unblock': 'Unblock',
    'admin.sendBulkNotification': 'Send Bulk Notification',
    'admin.enterMessage': 'Enter your message to all farmers...',
    'admin.sending': 'Sending...',
    'admin.sendToAll': 'Send to All Users',
    'admin.selectUsers': 'Select Users',
    'admin.selectUsersToNotify': 'Select Users to Notify:',
    'admin.sendToSelected': 'Send to Selected',
    'admin.selectAll': 'Select All',
    'admin.clear': 'Clear',
    'admin.recentNotifications': 'Recent Notifications',
    'admin.weatherAlertSent': 'Weather Alert Sent',
    'admin.sentToActiveUsers': 'Sent to {count} active users',
    'admin.systemMaintenanceNotice': 'System Maintenance Notice',
    'admin.sentToAllUsers': 'Sent to all users',
    'admin.hoursAgo': '{count} hours ago',
    'admin.dayAgo': '{count} day ago',
    'admin.systemLogs': 'System Logs',
    'admin.allLevels': 'All Levels',
    'admin.info': 'Info',
    'admin.error': 'Error',
    'admin.noLogsAvailable': 'No logs available',
    'admin.systemSettings': 'System Settings',
    'admin.automatedNotifications': 'Automated Notifications',
    'admin.dailyWeatherAlerts': 'Daily weather alerts and farming tips',
    'admin.enabled': 'Enabled',
    'admin.userRegistration': 'User Registration',
    'admin.allowNewFarmers': 'Allow new farmers to register',
    'admin.maintenanceMode': 'Maintenance Mode',
    'admin.temporarilyDisable': 'Temporarily disable app for maintenance',
    'admin.disabled': 'Disabled',
    'admin.adminSettings': 'Admin Settings',
    'admin.adminEmail': 'Admin Email',
    'admin.displayName': 'Display Name',
    'admin.clearSystemCache': 'Clear System Cache',
    'admin.databaseStatistics': 'Database Statistics',
    'admin.totalRecords': 'Total Records',
    'admin.storageUsed': 'Storage Used',
    'admin.apiCallsToday': 'API Calls Today',
    
    // User Settings
    'settings.userSettings': 'User Settings',
    'settings.notifications': 'Notifications',
    'settings.pushNotifications': 'Push Notifications',
    'settings.emailNotifications': 'Email Notifications',
    'settings.smsNotifications': 'SMS Notifications',
    'settings.contactInformation': 'Contact Information',
    'settings.phoneNumberLesotho': 'Phone Number (Lesotho)',
    'settings.edit': 'Edit',
    'settings.cancel': 'Cancel',
    'settings.save': 'Save',
    'settings.saving': 'Saving...',
    'settings.saveSettings': 'Save Settings',
    'settings.settingsSaved': 'Settings saved successfully!',
    'settings.settingsFailed': 'Failed to save settings. Please try again.',
    'settings.enterExactly8Digits': 'Please enter exactly 8 digits after +266',
    
    // Header
    'header.settings': 'Settings',
    'header.adminDashboard': 'Admin Dashboard',
    
    // Auth Modal
    'auth.welcomeToAgroSense': 'Welcome to AgroSense',
    'auth.online': 'Online',
    'auth.offlineMode': 'Offline Mode',
    'auth.secureAccess': 'Secure access to your personalized farming dashboard',
    'auth.worksOffline': 'Works offline - your data will sync when you\'re back online',
    'auth.continueWithGoogle': 'Continue with Google',
    'auth.continueWithGitHub': 'Continue with GitHub',
    'auth.or': 'or',
    'auth.youreOffline': 'You\'re offline.',
    'auth.signInWillWork': 'Sign in will work locally and sync when you\'re back online.',
    'auth.accountCreationWillWork': 'Account creation will work locally and sync when you\'re back online.',
    'auth.signIn': 'Sign In',
    'auth.signUp': 'Sign Up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.fullName': 'Full Name',
    'auth.signingIn': 'Signing in...',
    'auth.creatingAccount': 'Creating Account...',
    'auth.createAccount': 'Create Account',
    'auth.dataEncrypted': 'Your data is encrypted and secure',
    'auth.accountCreated': 'Account created! Please check your email to verify your account.',
    'auth.accountCreatedOffline': 'Account created offline! You can start using the app immediately.',
    'auth.authServiceNotConfigured': 'Authentication service is not configured.',
    'auth.oauthFailed': 'OAuth authentication failed.',
    'auth.signInFailed': 'Sign in failed',
    'auth.accountCreationFailed': 'Account creation failed',

  },
  st: {
    // Header
    'header.appName': 'AgroSense',
    'header.tagline': 'Moeletsi oa Temo e Bohlale',
    'header.online': 'Inthaneteng',
    'header.offline': 'Ha ho Inthanete',
    'header.signIn': 'Kena',
    'header.signOut': 'Tsoa',
    'header.admin': 'Motsamaisi',
    'header.sendNotification': 'Romela Tsebiso',
    'header.notificationsOn': 'Litsebiso li Bulehile',
    'header.notificationsOff': 'Litsebiso li Koetsoe',
    
    // Dashboard
    'dashboard.title': 'Boto ea Taolo',
    'dashboard.welcome': 'Rea u amohela ho AgroSense',
    'dashboard.myFarms': 'Lipolasa tsa Ka',
    'dashboard.addFarm': 'Eketsa Polasa',
    'dashboard.weather': 'Boemo ba Leholimo',
    'dashboard.cropHealth': 'Bophelo bo Botle ba Lijalo',
    'dashboard.aiAdvisor': 'Moeletsi oa AI',
    'dashboard.marketInfo': 'Tlhahisoleseling ea Mmaraka',
    'dashboard.taskPlanner': 'Moralo oa Mesebetsi',
    'dashboard.yieldForecast': 'Ponelopele ea Kotulo',
    'dashboard.soilManagement': 'Tsamaiso ea Mobu',
    'dashboard.irrigation': 'Nosetso',
    'dashboard.fertilizerPlanner': 'Moralo oa Monontsha',
    'dashboard.pestControl': 'Taolo ea Likokoanyana',
    'dashboard.visibility': 'km Pono',
    'dashboard.pestWeatherAlerts': 'Litsebiso tsa Likokoanyana le Leholimo',
    'dashboard.authRequired': 'Litsebiso tsa likokoanyana le leholimo li hloka netefatso.',
    'dashboard.personalizedRecommendations': 'Likeletso tse Ikgethileng',
    'dashboard.personalizedAdvice': 'Likeletso tsa hau tse ikgethileng tsa temo li tla hlaha mona.',
    'dashboard.signInAdvice': 'Kena ho fumana likeletso tse ikgethileng tsa temo',
    'dashboard.irrigationReminder': 'Khopotso ea Nosetso',
    'dashboard.monitorSoil': 'Sheba maemo a mongobo oa mobu bakeng sa bophelo bo botle ba lijalo.',
    'dashboard.weatherAlert': 'Tsebiso ea Leholimo',
    'dashboard.signInRecommendations': 'Kena bakeng sa Likeletso tse Ikgethileng',
    'dashboard.cropHealthAnalysis': 'Tlhahlobo ea Bophelo bo Botle ba Lijalo',
    'dashboard.uploadPhotos': 'Jarisa lifoto tsa lijalo tsa hau ho fumana tlhahlobo ea AI le likeletso tsa kalafo.',
    'dashboard.addCropReport': '+ Eketsa Tlaleho ea Bophelo ba Lijalo',
    'dashboard.signInAnalyze': 'Kena ho Hlahloba Lijalo',
    'dashboard.signInQuestions': 'Kena ho Botsa Lipotso',
    'dashboard.noFarmSelected': 'Ha ho polasa e khethiloeng bakeng sa phanele ea nosetso.',
    'dashboard.live': 'Tobileng',
    'dashboard.currentWeather': 'Boemo ba Leholimo ba Hona Joale',
    'dashboard.farmLocation': 'Sebaka sa Polasa',
    'dashboard.temperature': 'Mocheso',
    'dashboard.humidity': 'Mongobo',
    'dashboard.wind': 'Moea (km/h)',
    
    // Stats
    'stats.activeFarms': 'Lipolasa tse Sebetsang',
    'stats.totalArea': 'Sebaka Kaofela',
    'stats.regions': 'Libaka',
    'stats.mostRecentFarm': 'Polasa ea Morao-rao',
    
    // Features
    'features.cropAnalysis': 'Tlhahlobo ea Lijalo',
    'features.weather': 'Leholimo',
    'features.pestControl': 'Taolo ea Likokoanyana',
    'features.voiceQuery': 'Potso ea Lentsoe',
    'features.signInRequired': 'Ho hlokahala ho kena',
    
    // AI Advisor
    'ai.title': 'Moeletsi oa Temo oa AI',
    'ai.subtitle': 'Mpotse eng kapa eng ka temo',
    'ai.placeholder': 'Mpotse eng kapa eng ka temo...',
    'ai.examples': 'Mehlala: "Lijalo tse ntle bakeng sa nako ea pula", "Ke etse joang ha ke na le likokoanyana?"',
    'ai.poweredBy': 'E tsamaisoa ke Cohere AI',
    'ai.typing': 'AI e ntse e nahana...',
    'ai.welcome': 'Lumela Molemirui!',
    'ai.welcomeText': 'Ke mothusi oa hau oa temo oa AI. Mpotse ka lijalo, boemo ba leholimo, likokoanyana, kapa eng kapa eng e amanang le temo. Nka u thusa hape ka mekhoa e metle le keletso ea temo ka kakaretso.',
    'ai.startTyping': 'Qala ka ho ngola potso ea hau lebokoseng le ka tlase.',
    'ai.myChats': 'Lipuisano tsa Ka',
    'ai.newChat': 'Puisano e Ncha',
    'ai.newFarmingChat': 'Puisano e Ncha ea Temo',
    'ai.you': 'Uena',
    'ai.ai': 'AI',
    'ai.smartFarmingAI': '🌾 AI ea Temo e Bohlale',
    'ai.intelligentAssistant': 'Mothusi oa bohlale oa temo',
    'ai.researchOn': 'Liphuputso li Bulehile',
    'ai.researchOff': 'Liphuputso li Koetsoe',
    'ai.exportChat': 'Romela puisano',
    'ai.hideHistory': 'Pata nalane ea puisano',
    'ai.showHistory': 'Bontša nalane ea puisano',
    'ai.stopRecording': 'Emisa ho rekota',
    'ai.startRecording': 'Qala ho rekota lentsoe',
    'ai.sendMessage': 'Romela molaetsa',
    'ai.enterToSend': 'Tobetsa Enter ho romela • Shift+Enter bakeng sa mola o mocha',
    'ai.offline': 'Ha ho Inthanethe',
    'ai.retrying': 'E ntse e leka hape...',
    'ai.sources': 'Mehloli:',
    'ai.messages': 'melaetsa',
    
    // Fertilizer
    'fertilizer.title': 'Moralo oa Monontsha',
    'fertilizer.subtitle': 'Ntlafatsa phepo ea lijalo tsa hau',
    'fertilizer.signInRequired': 'Kena ho fumana likeletso tsa monontsha',
    'fertilizer.signInButton': 'Kena ho Tsoela Pele',
    'fertilizer.calculating': 'E ntse e bala likeletso...',
    'fertilizer.noFarms': 'Ha ho Lipolasa',
    'fertilizer.addFarms': 'Eketsa lipolasa ho fumana likeletso tsa monontsha',
    'fertilizer.dataError': 'Ha ho Khone ho Jara Lintlha',
    'fertilizer.refreshData': 'Nchafatsa Lintlha',
    'fertilizer.noRecommendations': 'Ha ho likeletso tse fumanehang bakeng sa lipolasa tsa hau.',
    'fertilizer.ensureReadings': 'Netefatsa hore ho na le lipalo tsa mobu.',
    'fertilizer.retryRecommendations': 'Leka Hape Likeletso',
    'fertilizer.soilImbalance': 'Mobu ha o Lekane',
    'fertilizer.lastReading': 'Palo ea ho qetela:',
    'fertilizer.currentNutrients': 'Maemo a Phepo a Hona Joale',
    'fertilizer.recommendedApplication': 'Tšebeliso e Khothaletsoang',
    'fertilizer.urea': 'Urea (46-0-0)',
    'fertilizer.dap': 'DAP (18-46-0)',
    'fertilizer.mop': 'MOP (0-0-60)',
    'fertilizer.kg': 'kg/ha',
    'fertilizer.bestPractices': 'Mekhoa e Metle',
    'fertilizer.practice1': 'Sebelisa monontsha nakong ea boemo bo phodileng',
    'fertilizer.practice2': 'Nosetsa lijalo hang-hang kamora ho sebelisa monontsha',
    'fertilizer.practice3': 'Arohanya tšebeliso ea naetrojene bakeng sa ho nka hantle',
    'fertilizer.practice4': 'Hlahloba mobu khafetsa ho sheba maemo a phepo',
    'fertilizer.practice5': 'Boloka monontsha sebakeng se phodileng le se omileng',
    
    // Common
    'common.loading': 'E ntse e jara...',
    'common.error': 'Phoso',
    'common.save': 'Boloka',
    'common.cancel': 'Hlakola',
    'common.delete': 'Hlakola',
    'common.edit': 'Fetola',
    'common.add': 'Eketsa',
    'common.close': 'Koala',
    'common.submit': 'Romela',
    'common.search': 'Batla',
    'common.filter': 'Sefa',
    'common.sort': 'Hlophisa',
    'common.view': 'Sheba',
    'common.download': 'Jarolla',
    'common.upload': 'Jarisa',
    'common.refresh': 'Nchafatsa',
    'common.back': 'Khutlela',
    'common.next': 'E latelang',
    'common.previous': 'E fetileng',
    'common.yes': 'Ee',
    'common.no': 'Che',
    'common.ok': 'Ho lokile',
    'common.language': 'st',
    'common.welcome': 'Rea u amohela',
    'common.realTimeData': 'Lintlha tsa nako ea sebele',
    'common.cachedData': 'Lintlha tse bolokiloeng',
    'common.quickActions': 'Liketso tse Potlakang',
    'common.addPestAlert': 'Eketsa Tsebiso ea Likokoanyana',
    'common.viewHistory': 'Sheba Nalane ea Tlhahlobo',
    'common.statistics': 'Lipalo-palo',
    'common.features': 'Litsšebeletso',
    'common.yourFarms': 'Lipolasa tsa Hau',
    'common.viewFarm': 'Sheba polasa',
    'common.noLocation': 'Ha ho sebaka',
    'common.acres': 'li-acre',
    'common.recentReports': 'Litlaleho tsa Morao-rao',
    'common.noReports': 'Ha ho litlaleho haesale.',
    'common.unknownFarm': 'Polasa e sa Tsejoeng',
    'common.healthy': 'E Phetseng Hantle',
    'common.needsAttention': 'E Hloka Tlhokomelo',
    'common.diseased': 'E Kulang',
    'common.pestDetected': 'Likokoanyana li Fumanoe',
    'dashboard.navigationTabs': 'Tataiso ea Dashboard',
    
    // Footer
    'footer.empowering': 'Ho matlafatsa lihoai ka lintlha tse tsoang ho AI bakeng sa tlhokomelo e ntle ea lijalo le temo e tšoarellang',
    'footer.quickLinks': 'Lihokelo tse Potlakang',
    'footer.cropAnalysis': 'Tlhahlobo ea Lijalo',
    'footer.weatherForecast': 'Ponelopele ea Leholimo',
    'footer.pestManagement': 'Tsamaiso ea Likokoanyana',
    'footer.aiAdvisor': 'Moeletsi oa AI',
    'footer.resources': 'Lisebelisoa',
    'footer.farmingGuide': 'Tataiso ea Temo',
    'footer.bestPractices': 'Mekhoa e Metle',
    'footer.communityForum': 'Seboka sa Sechaba',
    'footer.supportCenter': 'Setsi sa Tšehetso',
    'footer.contactUs': 'Ikopanye le Rona',
    'footer.support': 'tšehetso@agrosense.com',
    'footer.phone': '+1 (555) 123-4567',
    'footer.address': 'Setsi sa Boqapi ba Temo',
    'footer.copyright': '© 2024 AgroSense. Litokelo tsohle li sirelelitsoe. E hahiloe bakeng sa balemirui, ke balemirui.',
    'footer.privacyPolicy': 'Leano la Lekunutu',
    'footer.termsOfService': 'Lipehelo tsa Tšebeletso',
    'footer.help': 'Thuso',
    
    // Weather Panel
    'weather.resultsFor': 'Liphetho tsa',
    'weather.changeLocation': '(Fetola sebaka)',
    'weather.feelsLike': 'E utloisisa e kang',
    'weather.precip': 'Pula',
    'weather.wind': 'Moea',
    'weather.chooseLocation': 'Khetha sebaka',
    'weather.searchCity': 'Batla toropo...',
    'weather.popularLocations': 'Libaka tse tummeng:',
    'weather.useMyLocation': 'Sebelisa sebaka sa ka',
    'weather.weatherUpdate': 'Boemo ba leholimo bo tla nchafatsoa bakeng sa sebaka seo u se khethileng',
    'weather.loadingPrices': 'E jara litheko tsa mmaraka, ka kopo ema...',
    'weather.overview': 'kakaretso',
    'weather.precipitation': 'pula',
    'weather.humidity': 'mongobo',
    'weather.now': 'Hona Joale',
    'weather.todaysAmount': 'Tekanyo ea kajeno',
    'weather.todaysHigh': 'Phahameng ea kajeno',
    'weather.todaysAverage': 'Karolelano ea kajeno',
    'weather.clear': 'E Hlakile',
    'weather.partlyCloudy': 'Maru a Seng',
    'weather.cloudy': 'Maru',
    'weather.fog': 'Moholi',
    'weather.drizzle': 'Pula e Nyane',
    'weather.rainShowers': 'Lipula',
    'weather.thunderstorm': 'Ledimo',
    'weather.snow': 'Lehloa',
    'weather.rain': 'Pula',
    'weather.unknown': 'Ha ho Tsejoe',
    
    // Market Info
    'market.title': 'Limmaraka tsa Poone tsa Lesotho',
    'market.subtitle': 'Litheko tsa poone tsa nako ea sebele ho pholletsa le limmaraka tsa Lesotho',
    'market.refreshData': 'Nchafatsa Litheko',
    'market.bestMarket': 'Mmaraka o Motle ka ho Fetisisa',
    'market.highestPrice': 'Theko e phahameng ea poone',
    'market.priceStability': 'Botsitso ba Theko',
    'market.marketConditions': 'Maemo a mmaraka',
    'market.recommendedAction': 'Ketso e Khothaletsoang',
    'market.basedOnTrends': 'Ho latela mekhoa',
    'market.loadingPrices': 'E jara litheko tsa mmaraka, ka kopo ema...',
    'market.errorFetching': 'Phoso ea ho fumana lintlha:',
    'market.noData': 'Ha ho lintlha tsa litheko tsa mmaraka tse fumanehang nakong ena.',
    'market.tryRefresh': 'Leka ho nchafatsa kapa khutlela hamorao.',
    'market.commodity': 'Thepa',
    'market.market': 'Mmaraka',
    'market.price': 'Theko',
    'market.unit': 'Yuniti',
    'market.date': 'Letsatsi',
    
    // Yield Forecast
    'yield.title': 'Ponelopele ea Kotulo',
    'yield.signInRequired': 'Kena ho fumana Ponelopele ea Kotulo.',
    'yield.cropType': 'Mofuta oa Lijalo',
    'yield.area': 'Sebaka (li-acre)',
    'yield.getForecast': 'Fumana Ponelopele',
    'yield.forecasting': 'E ntse e etsa ponelopele...',
    'yield.error': 'Phoso:',
    'yield.predictedYield': 'Kotulo e lebeletsoeng ea',
    'yield.over': 'ho',
    'yield.acres': 'li-acre',
    'yield.maize': 'Poone',
    'yield.wheat': 'Koro',
    'yield.sorghum': 'Mabele',
    
    // Task Planner
    'tasks.title': 'Moralo oa Mesebetsi ea Polasa',
    'tasks.subtitle': 'Hlophisa mesebetsi ea hau ea temo ka mokhoa o nepahetseng',
    'tasks.signInRequired': 'Ka kopo kena ho fumana mesebetsi ea hau ea moralo oa polasa.',
    'tasks.addNewTask': 'Eketsa Mosebetsi o Mocha oa Polasa',
    'tasks.createNewTask': 'Theha Mosebetsi o Mocha oa Polasa',
    'tasks.taskTitle': 'Sehlooho sa Mosebetsi',
    'tasks.description': 'Tlhaloso',
    'tasks.dueDate': 'Letsatsi la ho Qetela',
    'tasks.required': '*',
    'tasks.titlePlaceholder': 'mohlala: Jala poone tšimong A',
    'tasks.descriptionPlaceholder': 'Lintlha tsa mosebetsi, sebaka, kapa lintlha',
    'tasks.createTask': 'Theha Mosebetsi',
    'tasks.adding': 'E ntse e eketsa...',
    'tasks.pendingTasks': 'Mesebetsi e Emetsoeng',
    'tasks.completedTasks': 'Mesebetsi e Phethiloeng',
    'tasks.noPendingTasks': 'Ha ho mesebetsi e emetsoeng',
    'tasks.addFirstTask': 'Eketsa mosebetsi oa hau oa pele oa polasa ho qala',
    'tasks.loadingTasks': 'E jara mesebetsi ea polasa...',
    'tasks.taskError': 'Phoso ea Mosebetsi',
    'tasks.today': 'Kajeno',
    'tasks.tomorrow': 'Hosane',
    'tasks.yesterday': 'Maobane',
    'tasks.inDays': 'matsatsing a {days}',
    'tasks.daysAgo': 'matsatsi a {days} a fetileng',
    'tasks.save': 'Boloka',
    'tasks.edit': 'Fetola Mosebetsi',
    'tasks.delete': 'Hlakola Mosebetsi',
    'tasks.deleteConfirm': 'Na u na le bonnete ba hore u batla ho hlakola mosebetsi ona?',
    
    // Soil Management
    'soil.title': 'Tsamaiso ea Mobu',
    'soil.subtitle': 'Sheba le ho ntlafatsa bophelo bo botle ba mobu oa hau',
    'soil.signInRequired': 'Fumana litlaleho tse qaqileng tsa limatlafatsi a mobu le likeletso bakeng sa lipolasa tsa hau.',
    'soil.signInButton': 'Kena ho Laola Mobu',
    'soil.loadingData': 'E jara lintlha tsa mobu...',
    'soil.noFarms': 'Ha ho Lipolasa tse Fumanehang',
    'soil.addFarms': 'U hloka ho eketsa lipolasa profaeleng ea hau ho sheba lintlha tsa tsamaiso ea mobu.',
    'soil.dataError': 'Phoso ea ho Jara Lintlha Tsohle',
    'soil.tryAgain': 'Leka Hape',
    'soil.allFarms': 'Lipolasa Tsohle',
    'soil.averageNitrogen': 'Karolelano ea Naetrojene',
    'soil.averagePhosphorus': 'Karolelano ea Fosforo',
    'soil.averagePotassium': 'Karolelano ea Potasiamo',
    'soil.low': 'E Tlase',
    'soil.optimal': 'E Nepahetseng',
    'soil.yourFarms': 'Lipolasa tsa Hau',
    'soil.noReadings': 'Ha ho lipalo tsa mobu tse fumanehang bakeng sa lipolasa tsa hau.',
    'soil.reloadReadings': 'Jara Lipalo Hape',
    'soil.needsAttention': 'E Hloka Tlhokomelo',
    'soil.lastTested': 'E hlahlobiloe la ho qetela:',
    'soil.nitrogen': 'Naetrojene',
    'soil.phosphorus': 'Fosforo',
    'soil.potassium': 'Potasiamo',
    'soil.lowNutrients': 'Limatlafatsi tse le nngwe kapa ho feta li ka tlase ho maemo a nepahetseng. Nahana ka ho eketsa lintho tse matlafatsang.',
    'soil.healthRecommendations': 'Likeletso tsa Bophelo bo Botle ba Mobu',
    'soil.waterManagement': 'Tsamaiso ea Metsi',
    'soil.waterManagementDesc': 'Kenya tšebetsong nosetso e sebetsang ho boloka metsi le ho boloka maemo a tsitsitseng a mongobo oa mobu.',
    'soil.fertilizationPlan': 'Moralo oa Monontsha',
    'soil.fertilizationDesc': 'Sebelisa monontsha o lekaneng oa NPK ho latela liphetho tsa tlhahlobo ea mobu. Sebelisa komposte ea tlhaho ho boloka maemo a limatlafatsi.',
    'soil.cropRotation': 'Phetoho ea Lijalo',
    'soil.cropRotationDesc': 'Phetola lijalo tsa legume le cereals ho lokisa naetrojene ka tlhaho le ho senya mehato ea likokoanyana. Nahana ka ho jala lijalo tse koahelang.',
    'soil.testingSchedule': 'Lenane la Tlhahlobo',
    'soil.testingScheduleDesc': 'Etsa litlhahlobo tsa mobu khoeli e nngwe le e nngwe ho isa ho tse 6 bakeng sa tlhokomelo le tsamaiso e nepahetseng.',
    'soil.recentTests': 'Litlhahlobo tsa Morao-rao tsa Mobu',
    'soil.noRecentData': 'Ha ho lintlha tsa morao-rao tsa tlhahlobo ea mobu tse ka bontšoang.',
    'soil.farm': 'Polasa',
    'soil.date': 'Letsatsi',
    'soil.status': 'Boemo',
    'soil.critical': 'Bo Kotsi',
    
    // Privacy Settings
    'privacy.title': 'Litlhophiso tsa Lekunutu',
    'privacy.dataRetention': 'Polokelo ea Lintlha',
    'privacy.dataRetentionDesc': 'Khetha hore na u batla ho boloka lintlha tsa hau nako e kae',
    'privacy.analytics': 'Tlhahlobo ea Lintlha',
    'privacy.analyticsDesc': 'Lumella ho sebelisa lintlha tsa hau bakeng sa ntlafatso ea tšebeletso',
    'privacy.marketing': 'Papatso',
    'privacy.marketingDesc': 'Fumana litsebiso tsa lihlahisoa tse ncha le litšebeletso',
    'privacy.partners': 'Balekane',
    'privacy.partnersDesc': 'Arolelana lintlha le balekane ba tšepahalang bakeng sa tšebeletso e ntlafetseng',
    'privacy.saveSettings': 'Boloka Litlhophiso',
    'privacy.saved': 'E Bolokiloe!',
    'common.months': 'likhoeli',
    'common.year': 'selemo',
    'common.years': 'lilemo',
    'common.authRequired': 'Ho hlokahala netefatso',
    'common.takePhoto': 'Nka Setšoantšo',
    'common.capture': 'Nka',
    'common.analysisResult': 'Sephetho sa Tlhahlobo',
    'common.takeUploadPhoto': 'Nka kapa jarisa setšoantšo ho hlahloba bophelo ba lijalo',
    'common.analysisHistory': 'Nalane ea Tlhahlobo',
    'common.noAnalysisHistory': 'Ha ho nalane ea tlhahlobo haesale.',
    'common.lastAnalyses': 'Litlhahlobo tsa hau tse 3 tsa ho qetela li tla hlaha mona.',
    'common.home': 'Hae',
    
    // Offline Support
    'offline.online': 'Inthaneteng',
    'offline.offline': 'Ha ho Inthanethe',
    'offline.syncing': 'E ntse e Kopanya',
    'offline.connected': 'Ho Hokahane le Inthanethe',
    'offline.disconnected': 'Ha ho Inthanethe',
    'offline.workingOffline': 'E Sebetsa Ntle le Inthanethe',
    'offline.offlineMessage': 'U ka tsoela pele ho sebelisa AgroSense. Lintlha tsa hao li tla kopanngoa ha u khutlela inthaneteng.',
    'offline.pendingSync': 'Lintho tse {{count}} li emetse ho kopanngoa',
    'offline.syncMessage': 'Tsena li tla kenngoa ha u na le inthanethe.',
    'offline.allSynced': 'Lintlha tsohle li kopantsoe',
    'offline.syncedMessage': 'Lintlha tsa hao li ncha.',
    'offline.lastOnline': 'Qetellong inthaneteng',
    'offline.dataFromCache': 'Ho bontša lintlha tse bolokiloeng (offline)',
    'offline.willSyncLater': 'Liphetoho li tla kopanngoa ha ho na le inthanethe',
    
    // AI Error Messages
    'ai.error.pleaseEnterMessage': 'Ka kopo ngola molaetsa',
    'ai.error.messageTooLong': 'Molaetsa o molelele haholo (ho fihla ho litlhaku tse 2000)',
    'ai.error.useEnglishSesotho': 'Ka kopo sebelisa Senyesemane kapa Sesotho feela',
    'ai.error.useAppropriateLanguage': 'Ka kopo sebelisa puo e nepahetseng',
    'ai.error.speechNotSupported': 'Ho lemosa lentsoe ha ho tšehetsoe sebatli sena',
    'ai.error.micAccessDenied': 'Phihlello ea maekrofono e haniloe. Ka kopo lumella phihlello ea maekrofono.',
    'ai.error.noMicrophoneFound': 'Ha ho maekrofono o fumanehang. Ka kopo hokahanya maekrofono.',
    'ai.error.recordingFailed': 'Ho hloleha ho qala ho rekota. Ka kopo leka hape.',
    'ai.error.speechRequiresInternet': 'Ho lemosa lentsoe ho hloka khokahanyo ea inthanethe',
    'ai.error.transcriptionFailed': 'Ho hloleha ho fetolela molumo. Ka kopo leka ho ngola.',
    'ai.error.ttsNotSupported': 'Ho bua ha mongolo ha ho tšehetsoe sebatli sena',
    'ai.error.audioPlaybackFailed': 'Ho hloleha ho bapala molumo. Ka kopo leka hape.',
    'ai.error.noInternet': 'Ha ho khokahanyo ea inthanethe. Ka kopo hlahloba marang-rang a hau.',
    'ai.error.tooManyRequests': 'Likopo tse ngata haholo. Ka kopo ema hanyenyane.',
    'ai.error.serviceUnavailable': 'Tšebeletso ea AI ha e fumaneha hajoale. Ka kopo leka hape.',
    'ai.error.invalidResponse': 'Karabo e sa nepahalang ho tsoa tšebeletso ea AI',
    'ai.error.networkRetry': 'Phoso ea marang-rang. E ntse e leka hape... ({count}/3)',
    'ai.error.failedToGetResponse': 'Ho hloleha ho fumana karabo ea AI',
    'ai.error.failedToCopy': 'Ho hloleha ho kopitsa molaetsa',
    'ai.error.voiceNotSupported': 'Lentsoe ha le tšehetsoe',
    'ai.error.audioRecordedImplementing': 'Molumo o rekotsoe - ho kenya tšebetsong phetolelo ea puo-ho-mongolo...',
    'ai.offlineMessage': 'AgroSense AI e sebetsa ntle le inthanethe ka likarabo tse bolokiloeng.',
    'ai.disclaimer': 'AgroSense AI e ka etsa liphoso. Nahana ka ho hlahloba tlhahisoleseling ea bohlokoa.',
    
    // Admin Dashboard
    'admin.dashboard': 'Boto ea Taolo ea Motsamaisi',
    'admin.welcome': 'Rea u amohela',
    'admin.manageUsers': 'Laola sethala sa AgroSense le basebelisi',
    'admin.refresh': 'Nchafatsa',
    'admin.overview': 'Kakaretso',
    'admin.users': 'Basebelisi',
    'admin.notifications': 'Litsebiso',
    'admin.logs': 'Lirekoto',
    'admin.settings': 'Litlhophiso',
    'admin.accessDenied': 'Phihlello e Haniloe',
    'admin.noPrivileges': 'Ha u na litokelo tsa motsamaisi ho fumana boto ena ea taolo.',
    'admin.loadingData': 'E jara lintlha tsa motsamaisi...',
    'admin.totalUsers': 'Basebelisi Kaofela',
    'admin.activeUsers': 'Basebelisi ba Sebetsang',
    'admin.blockedUsers': 'Basebelisi ba Thibetsoeng',
    'admin.notificationsSent': 'Litsebiso tse Romelitsoeng',
    'admin.systemHealth': 'Bophelo bo Botle ba Sisteme',
    'admin.healthy': 'e phetseng hantle',
    'admin.warning': 'temoso',
    'admin.critical': 'e kotsi',
    'admin.searchUsers': 'Batla basebelisi...',
    'admin.allUsers': 'Basebelisi Bohle',
    'admin.active': 'Ba Sebetsang',
    'admin.blocked': 'Ba Thibetsoeng',
    'admin.user': 'Mosebelisi',
    'admin.contact': 'Ikopanyo',
    'admin.status': 'Boemo',
    'admin.actions': 'Liketso',
    'admin.joined': 'A kena',
    'admin.block': 'Thibela',
    'admin.unblock': 'Lokolla',
    'admin.sendBulkNotification': 'Romela Tsebiso e Kholo',
    'admin.enterMessage': 'Kenya molaetsa oa hau ho balemirui bohle...',
    'admin.sending': 'E ntse e romela...',
    'admin.sendToAll': 'Romela ho Basebelisi Bohle',
    'admin.selectUsers': 'Khetha Basebelisi',
    'admin.selectUsersToNotify': 'Khetha Basebelisi ba lokelang ho Tsebisoa:',
    'admin.sendToSelected': 'Romela ho ba Khethiloeng',
    'admin.selectAll': 'Khetha Bohle',
    'admin.clear': 'Hlakola',
    'admin.recentNotifications': 'Litsebiso tsa Morao-rao',
    'admin.weatherAlertSent': 'Tsebiso ea Leholimo e Romelitsoe',
    'admin.sentToActiveUsers': 'E romelitsoe ho basebelisi ba {count} ba sebetsang',
    'admin.systemMaintenanceNotice': 'Tsebiso ea Tlhokomelo ea Sisteme',
    'admin.sentToAllUsers': 'E romelitsoe ho basebelisi bohle',
    'admin.hoursAgo': 'lihora tse {count} tse fetileng',
    'admin.dayAgo': 'letsatsi le {count} le fetileng',
    'admin.systemLogs': 'Lirekoto tsa Sisteme',
    'admin.allLevels': 'Maemo Ohle',
    'admin.info': 'Tlhahisoleseling',
    'admin.error': 'Phoso',
    'admin.noLogsAvailable': 'Ha ho lirekoto tse fumanehang',
    'admin.systemSettings': 'Litlhophiso tsa Sisteme',
    'admin.automatedNotifications': 'Litsebiso tse Iketsang',
    'admin.dailyWeatherAlerts': 'Litsebiso tsa leholimo tsa letsatsi le letsatsi le likeletso tsa temo',
    'admin.enabled': 'E Bulehile',
    'admin.userRegistration': 'Ngoliso ea Basebelisi',
    'admin.allowNewFarmers': 'Lumella balemirui ba bacha ho ingolisa',
    'admin.maintenanceMode': 'Mokhoa oa Tlhokomelo',
    'admin.temporarilyDisable': 'Thibela sesebelisoa ka nako e khutšoanyane bakeng sa tlhokomelo',
    'admin.disabled': 'E Koetsoe',
    'admin.adminSettings': 'Litlhophiso tsa Motsamaisi',
    'admin.adminEmail': 'Imeile ea Motsamaisi',
    'admin.displayName': 'Lebitso la ho Bonts\'a',
    'admin.clearSystemCache': 'Hlakola Cache ea Sisteme',
    'admin.databaseStatistics': 'Lipalo-palo tsa Database',
    'admin.totalRecords': 'Lirekoto Kaofela',
    'admin.storageUsed': 'Polokelo e Sebelisoang',
    'admin.apiCallsToday': 'Lihokelo tsa API Kajeno',
    
    // User Settings
    'settings.userSettings': 'Litlhophiso tsa Mosebelisi',
    'settings.notifications': 'Litsebiso',
    'settings.pushNotifications': 'Litsebiso tse Sutumelang',
    'settings.emailNotifications': 'Litsebiso tsa Imeile',
    'settings.smsNotifications': 'Litsebiso tsa SMS',
    'settings.contactInformation': 'Tlhahisoleseling ea Ikopanyo',
    'settings.phoneNumberLesotho': 'Nomoro ea Mohala (Lesotho)',
    'settings.edit': 'Fetola',
    'settings.cancel': 'Hlakola',
    'settings.save': 'Boloka',
    'settings.saving': 'E ntse e boloka...',
    'settings.saveSettings': 'Boloka Litlhophiso',
    'settings.settingsSaved': 'Litlhophiso li bolokiloe ka katleho!',
    'settings.settingsFailed': 'Ho hloleha ho boloka litlhophiso. Ka kopo leka hape.',
    'settings.enterExactly8Digits': 'Ka kopo kenya linomoro tse 8 tse nepahetseng kamora +266',
    
    // Header
    'header.settings': 'Litlhophiso',
    'header.adminDashboard': 'Boto ea Taolo ea Motsamaisi',
    
    // Auth Modal
    'auth.welcomeToAgroSense': 'Rea u amohela ho AgroSense',
    'auth.online': 'Inthaneteng',
    'auth.offlineMode': 'Mokhoa oa Offline',
    'auth.secureAccess': 'Phihlello e sireletsehileng ho boto ea hau ea temo e ikgethileng',
    'auth.worksOffline': 'E sebetsa ntle le inthanethe - lintlha tsa hau li tla kopanngoa ha u khutlela inthaneteng',
    'auth.continueWithGoogle': 'Tsoela pele ka Google',
    'auth.continueWithGitHub': 'Tsoela pele ka GitHub',
    'auth.or': 'kapa',
    'auth.youreOffline': 'U se u le offline.',
    'auth.signInWillWork': 'Ho kena ho tla sebetsa lehae ebe ho kopanngoa ha u khutlela inthaneteng.',
    'auth.accountCreationWillWork': 'Ho theha akhaonto ho tla sebetsa lehae ebe ho kopanngoa ha u khutlela inthaneteng.',
    'auth.signIn': 'Kena',
    'auth.signUp': 'Ingolise',
    'auth.email': 'Imeile',
    'auth.password': 'Phasewete',
    'auth.fullName': 'Lebitso le Feletseng',
    'auth.signingIn': 'E ntse e kena...',
    'auth.creatingAccount': 'E ntse e Theha Akhaonto...',
    'auth.createAccount': 'Theha Akhaonto',
    'auth.dataEncrypted': 'Lintlha tsa hau li sireletsehile le ho khomarela',
    'auth.accountCreated': 'Akhaonto e theile! Ka kopo sheba imeile ea hau ho netefatsa akhaonto ea hau.',
    'auth.accountCreatedOffline': 'Akhaonto e theile offline! U ka qala ho sebelisa sesebelisoa hang-hang.',
    'auth.authServiceNotConfigured': 'Tšebeletso ea netefatso ha e hlophisoe.',
    'auth.oauthFailed': 'Netefatso ea OAuth e hloleile.',
    'auth.signInFailed': 'Ho kena ho hloleile',
    'auth.accountCreationFailed': 'Ho theha akhaonto ho hloleile',

  }
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('agrosense-language');
    return (saved as Language) || 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('agrosense-language', lang);
  };

  const t = (key: string, params?: Record<string, any>): string => {
    let text = translations[language][key as keyof typeof translations['en']] || key;
    
    // Simple interpolation for parameters like {count}, {days}, etc.
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      });
    }
    
    return text;
  };

  useEffect(() => {
    document.documentElement.lang = language === 'st' ? 'st' : 'en';
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}